import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { ChartCanvas, Chart, ZoomButtons, ClickCallback } from "@chartLib";
import {
  MouseCoordinateX,
  MouseCoordinateY,
  PriceCoordinate,
} from "@chartLib/lib/coordinates";
import { LineSeries, IchimokuFutureCloudOverlay } from "@chartLib/lib/series";
import { timeFormat } from "d3-time-format";
import { buildChartKey } from "./chartKey.js";
import { SeriesRenderer } from "./SeriesRenderer";
import IndicatorRenderer from "./IndicatorRenderer";
import XAxisBase from "./XAxisBase";
import YAxisBase from "./YAxisBase";
import CrossHair from "./CrossHair";
import InteractiveLayer from "./InteractiveLayer";
import ChartTooltips from "../ChartTooltips";
import MeasurementOverlay from "./MeasurementOverlay";
import { useThemeStore, getThemeColors } from "../../../store/themeStore";
import { useSettingsStore } from "../../../store/settingsStore";
import useWorkspaceStore from "../../../store/workspaceStore";
import indicatorRegistry from "../../../Registry/Indicators/IndicatorRegistry";
import marketdata from "../../../data/Services/marketdata";
import InteractiveOrderIntegration from "../../../chartLib/lib/interactive/InteractiveOrderIntegration";
import { globalToast } from "../../ToastProvider.jsx";
import OrderHoverIndicator from "../../../chartLib/lib/interactive/components/OrderHoverIndicator";
import { useOrderOverlay } from "../../../hooks/useOrderOverlay.js";
import {
  useOrderDefaults,
  useOrderDefaultsStore,
} from "../../../hooks/useOrderDefaults.js";
import { useOrderStore } from "../../../store/orderStore.jsx";
import { formatPrice } from "../../../utils/priceUtils.js";
import {
  determineOrderType,
  shouldOrderTypeCrossover,
} from "../../../utils/orderTypeLogic.js";
import {
  createEnhancedYExtents,
  filterOrdersBySymbol,
  getOrdersExtents,
  getPositionExtents,
} from "../../../utils/chartExtents";
import { usePositionData } from "../../../hooks/usePositionData";
import { router } from "../../../data/Services/ordersRouter.jsx";
import { submitOrderToRouter } from "../../../utils/orderSubmission.js";
import { confirmNewOrder } from "../../../utils/confirmationUtils.js";
import { buildOtoLegs } from "../../../utils/otoLegs.js";
import { useKeyboardStateStore } from "../../../store/keyboardStateStore";
import ChartBottomIndicators from "./ChartBottomIndicators";
import CompareLegend from "./CompareLegend";
import { getCompareBaseline, priceToPercent } from "../../../store/compare";
import { addEventListenerAllWindows } from "~/utils/popoutWindowsRegistry";
import { isFeatureEnabled } from "../../../data/Services/settings";
import {
  useHoverInfoStore,
  computeHoverExtras,
} from "../../../store/chart/hoverInfoStore";

const CANVAS_PADDING = { left: 0, right: 15, top: 0, bottom: 0 };
const CANVAS_MARGIN = { left: 0, right: 55, top: 0, bottom: 30 };

// Helper function to format date based on periodicity
const formatDateByPeriodicity = (date, periodicity) => {
  try {
    const dateObj = new Date(date);

    // Define intraday periods that should show time
    const intradayPeriods = [
      "1min",
      "2min",
      "3min",
      "4min",
      "5min",
      "10min",
      "15min",
      "20min",
      "30min",
      "60min",
      "120min",
      "240min",
    ];
    const isIntraday = intradayPeriods.includes(periodicity);

    if (isIntraday) {
      // For intraday: show date + time
      return dateObj.toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      // For daily and above: show date only
      return dateObj.toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  } catch {
    return date.toString();
  }
};

/**
 * Determines if an indicator should use price axis formatting.
 *
 * @param {Object} indicator - The indicator configuration from registry
 * @returns {boolean} - true if should use price axis, false for custom scale
 *
 * Priority:
 * 1. Explicit isPriceAxis property in indicator (REQUIRED for all indicators)
 * 2. Heuristic fallback: overlay indicators (requireNewAxis: false) use price axis
 *
 * NOTE: All indicators MUST define isPriceAxis in their registry entry.
 */
const isPriceAxisIndicator = (indicator) => {
  if (!indicator) return true; // Default: price axis

  // 1. Priority: Check explicit isPriceAxis property
  if (indicator.isPriceAxis !== undefined) {
    return indicator.isPriceAxis;
  }

  // 2. Heuristic fallback: Indicators that share main axis typically use price formatting
  // Indicators with separate panels typically use custom formatting
  if (!indicator.requireNewAxis && !indicator.separatePanel) {
    return true; // Overlay on price axis
  }

  // Default for separate panel indicators: custom scale (not price)
  return false;
};

const ChartRenderer = React.forwardRef((props, ref) => {
  const {
    chartId,
    width,
    height,
    ratio,
    type,
    chartState,
    chartConfigs,
    layoutSnapshot,
    orderedCharts,
    chartSymbol,
    periodicity,
    showGrid,
    indicatorMenuRef,
    chartMenuRef,
    handleInteractiveContextMenu,
    handleOpenEditModal,
    handleMainInteractiveApiReady,
    handleInteractiveRemove,
    handleRemoveIndicator,
    resetKey,
    handleReset,
    isPointerInsideChartPanel,
    handleChartCanvasVisibleBarsChange,
    handleChartCanvasXExtentsChange,
    handleLoadMore,
    moveSectionUp,
    moveSectionDown,
    handleStartResize,
    handleResize,
    handleEndResize,
    perfMonitor, // Performance monitor (optional)
    renkoConfigHash, // Hash for forcing re-mount on Renko config change
    pointFigureConfigHash, // Hash for forcing re-mount on P&F config change
    // Compare mode
    isCompareMode = false,
    comparisonSymbols = [],
    removeComparisonSymbol,
    clearComparisons,
  } = props;
  const theme = useThemeStore((state) => state.theme);
  const themeColors = getThemeColors(theme);
  const crosshairEnabled = useThemeStore(
    (state) => state.crosshairEnabled ?? true,
  );

  // Settings store for one-click trading and indicator labels configuration
  const oneClickTrading = useSettingsStore((state) => state.oneClickTrading);
  const showIndicatorLabels = useSettingsStore(
    (state) => state.showIndicatorLabels ?? true,
  );
  const zoomButtonsAlwaysVisible = useSettingsStore(
    (state) => state.zoomButtonsAlwaysVisible ?? false,
  );
  const barCountdownVisibilityMode = useSettingsStore(
    (state) => state.barCountdownVisibilityMode ?? "hover",
  );
  const priceChangeVisibilityMode = useSettingsStore(
    (state) => state.priceChangeVisibilityMode ?? "hover",
  );
  const crosshairWidth = useSettingsStore((state) => state.crosshairWidth ?? 3);
  const modifierKeyDisarming = useSettingsStore(
    (state) => state.modifierKeyDisarming ?? false,
  );
  const showIchimokuFutureProjection = useSettingsStore(
    (state) => state.showIchimokuFutureProjection ?? true,
  );

  // Refs to ensure click handler always reads latest settings values
  // (avoids stale closures in chart library's subscription pipeline where SCU returns false)
  const oneClickTradingRef = React.useRef(oneClickTrading);
  const modifierKeyDisarmingRef = React.useRef(modifierKeyDisarming);
  oneClickTradingRef.current = oneClickTrading;
  modifierKeyDisarmingRef.current = modifierKeyDisarming;

  // Keyboard state store for modifier key tracking (one-click trading)
  const shiftArmed = useKeyboardStateStore((s) => s.shiftArmed);
  const ctrlArmed = useKeyboardStateStore((s) => s.ctrlArmed);
  const metaArmed = useKeyboardStateStore((s) => s.metaArmed);
  const disarmKey = useKeyboardStateStore((s) => s.disarmKey);
  const setBlockedKey = useKeyboardStateStore((s) => s.setBlockedKey);
  const blockedKey = useKeyboardStateStore((s) => s.blockedKey);

  // Order overlay hook - pass symbol for symbol-based persistence
  const { orders, createOrder } = useOrderOverlay(chartSymbol);
  // Use selector to prevent re-renders on unrelated store changes
  const updateOrder = useOrderStore((state) => state.updateOrder);

  // Posição do símbolo do gráfico (linha de posição + extents do eixo Y) —
  // via usePositionData, a autoridade ÚNICA de lookup (findPositionForSymbol:
  // tradução do servidor → símbolo → aliases FUT/FUL, com receiver reativo de
  // accountData). A implementação inline que vivia aqui tinha um fallback por
  // PREFIXO de 3 letras que pintava posição de OUTRO papel no gráfico: PETR4
  // no gráfico da PETR3, ou vencimento-irmão no WIN — e ainda esticava o eixo Y
  // para acomodá-la (mesma classe do cross-paint do #356; desde #125). Também
  // dependia de `[chartSymbol, orders]` — pegava carona em mudanças de ordens
  // para reagir a accountData.
  const position = usePositionData(chartSymbol);

  // Order defaults hook for shared quantity
  const { getAdjustedQuantity } = useOrderDefaults();

  // Track order drag state for real-time price coordinate updates
  const [orderDragState, setOrderDragState] = useState(null);

  // Handle order drag state changes
  const handleOrderDragStateChange = (dragState) => {
    setOrderDragState(dragState);
  };

  // Disable crosshair while dragging orders for better UX
  const shouldShowCrosshair = crosshairEnabled && !orderDragState;

  const resolveVisibilityMode = useCallback(
    (mode) => {
      if (mode === "always") return true;
      if (mode === "never") return false;
      return isPointerInsideChartPanel;
    },
    [isPointerInsideChartPanel],
  );

  // Measure mode state (Issue #101)
  const isMeasureModeActive = useWorkspaceStore(
    (state) => state.isMeasureModeActive,
  );
  const [measureStartPoint, setMeasureStartPoint] = useState(null);
  const [measureEndPoint, setMeasureEndPoint] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);

  // Measure mode handlers - use mouseDown + mouseMove + document mouseup
  const isMeasuringRef = React.useRef(false);

  const handleMeasureMouseDown = useCallback(
    (moreProps, e) => {
      if (!isMeasureModeActive) return;
      const { mouseXY } = moreProps;
      setMeasureStartPoint({ x: mouseXY[0], y: mouseXY[1] });
      setMeasureEndPoint({ x: mouseXY[0], y: mouseXY[1] });
      setIsMeasuring(true);
      isMeasuringRef.current = true;
    },
    [isMeasureModeActive],
  );

  const handleMeasureMouseMove = useCallback(
    (moreProps, e) => {
      if (!isMeasureModeActive || !isMeasuringRef.current) return;
      const { mouseXY } = moreProps;
      setMeasureEndPoint({ x: mouseXY[0], y: mouseXY[1] });
    },
    [isMeasureModeActive],
  );

  // Info panel: publica a barra sob o cursor (+ refs de variação) DESTE gráfico.
  // Roda no mousemove já throttled pelo ChartCanvas e escreve num store isolado
  // (useHoverInfoStore) — nenhum chart re-renderiza (I5). getState() evita
  // subscrição reativa no hot path. No-op quando a feature está desligada.
  const handleInfoHover = useCallback(
    (moreProps) => {
      if (!isFeatureEnabled("infoPanel")) return;
      const bar = moreProps?.currentItem;
      if (!bar) return;
      // Guard de bar-change ANTES do trabalho caro. getCurrentItem devolve a
      // referência exata de plotData[idx], então `prev?.bar === bar` é o teste
      // de snap-na-barra. Sem isso, o computeHoverExtras (O(n) + new Date/barra)
      // rodaria a cada mousemove (~60/s) com o cursor parado na mesma barra (I5).
      const store = useHoverInfoStore.getState();
      const prev = store.hovered[chartId];
      if (store.lastHoveredChartId === chartId && prev?.bar === bar) return;
      // fullData, não plotData: o "Var. dia" varre para trás até achar barra de
      // OUTRO dia — na janela visível (plotData) o dia anterior pode nem estar
      // na tela (zoom no dia corrente) e a linha ficava "---" em intraday.
      const { prevClose, dayPrevClose } = computeHoverExtras(
        bar,
        moreProps?.fullData ?? moreProps?.plotData,
      );
      store.setHoveredBar(chartId, bar, { prevClose, dayPrevClose });
    },
    [chartId],
  );

  // Document-level mouseup to end measuring and clear overlay
  React.useEffect(() => {
    if (!isMeasureModeActive) return;

    const handleMouseUp = () => {
      if (isMeasuringRef.current) {
        isMeasuringRef.current = false;
        setIsMeasuring(false);
        setMeasureStartPoint(null);
        setMeasureEndPoint(null);
      }
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isMeasureModeActive]);

  // Format price for measurement display
  const formatMeasurePrice = useCallback(
    (price) => {
      return formatPrice(chartSymbol, price);
    },
    [chartSymbol],
  );

  // ── Unified chart context menu ──────────────────────────────────────────
  // Background right-click / long-press on any chart section. Gate = release
  // flag chartContextMenu (kill switch, read at USE time). Sempre ligado por
  // padrão — independe de esconder ou não os controles da navbar.
  const handleChartAreaContextMenu = useCallback(
    (moreProps, e) => {
      if (!isFeatureEnabled("chartContextMenu")) return;
      chartMenuRef?.current?.showChartMenu(moreProps, e);
    },
    [chartMenuRef],
  );

  // Right-click / long-press on an indicator series: unified menu (flag on)
  // or the legacy standalone indicator menu (flag off).
  const handleIndicatorSeriesContextMenu = useCallback(
    (moreProps, e, indicator) => {
      if (isFeatureEnabled("chartContextMenu") && chartMenuRef?.current) {
        chartMenuRef.current.showWithIndicator(indicator, moreProps, e);
        return;
      }
      indicatorMenuRef.current?.show(moreProps, e, indicator);
    },
    [chartMenuRef, indicatorMenuRef],
  );

  const [hoveredIndicator, setHoveredIndicator] = React.useState(null);
  const hoveredIndicatorRef = React.useRef(null);

  React.useEffect(() => {
    hoveredIndicatorRef.current = hoveredIndicator;
  }, [hoveredIndicator]);

  const handleIndicatorHover = React.useCallback((indicator) => {
    if (!indicator || !indicator.id) return;
    setHoveredIndicator((prev) =>
      prev && prev.id === indicator.id ? prev : { id: indicator.id },
    );
  }, []);

  const handleIndicatorUnHover = React.useCallback((indicatorId) => {
    if (!indicatorId) return;
    setHoveredIndicator((prev) =>
      prev && prev.id === indicatorId ? null : prev,
    );
  }, []);

  React.useEffect(() => {
    setHoveredIndicator(null);
    hoveredIndicatorRef.current = null;
  }, [chartId]);

  React.useEffect(() => {
    if (typeof handleRemoveIndicator !== "function") return;
    if (!chartId) return;

    const keyListener = (event) => {
      const current = hoveredIndicatorRef.current;
      if (!current || !current.id) return;
      if (event.defaultPrevented) return;

      const target = event.target;
      if (
        target &&
        (target.isContentEditable ||
          (target.tagName &&
            ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) ||
          (typeof target.getAttribute === "function" &&
            target.getAttribute("data-allow-delete") === "false"))
      ) {
        return;
      }

      const key = event.key;
      if (key !== "Delete" && key !== "Backspace") return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey)
        return;

      event.preventDefault();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      } else {
        event.stopPropagation();
      }

      handleRemoveIndicator(chartId, current.id);
      setHoveredIndicator(null);
    };

    // PoC #popout: attach in every window — keys land on the focused window.
    const detach = addEventListenerAllWindows("keydown", keyListener, true);
    return detach;
  }, [chartId, handleRemoveIndicator]);

  // Memoize displayFormat functions to prevent coordinate label ghosting
  // Create stable references for each config to prevent recreation on every render
  const displayFormatCache = React.useRef(new Map());

  const getDisplayFormat = React.useCallback(
    (config) => {
      const cacheKey = `${config.id}-${config.indicator?.id || "main"}-${config.indicator?.decimalPlaces || "auto"}`;

      if (!displayFormatCache.current.has(cacheKey)) {
        const format = (p) => {
          try {
            const isPriceAxis = config.indicator
              ? isPriceAxisIndicator(config.indicator)
              : true;
            if (isPriceAxis) {
              return formatPrice(chartSymbol, p);
            }
            // Volume-type axes: use K/M/B abbreviation to prevent label overflow
            const isVolume =
              config.indicator?.key === "volume" ||
              config.indicator?.isVolumeAxis ||
              config.indicators?.some(
                (ind) => ind.isVolumeAxis || ind.key === "volume",
              );
            if (isVolume) {
              const absVal = Math.abs(p ?? 0);
              if (absVal >= 1000000000)
                return ((p ?? 0) / 1000000000).toFixed(2) + "B";
              if (absVal >= 1000000)
                return ((p ?? 0) / 1000000).toFixed(2) + "M";
              if (absVal >= 1000) return ((p ?? 0) / 1000).toFixed(2) + "K";
              return Number(p ?? 0).toFixed(0);
            }
            const decimals = config.indicator?.decimalPlaces;
            if (decimals !== undefined) {
              return Number(p ?? 0).toFixed(decimals);
            }
            return Math.abs(p) >= 1 ? p.toFixed(2) : p.toFixed(4);
          } catch {
            return String(p);
          }
        };
        displayFormatCache.current.set(cacheKey, format);
      }

      return displayFormatCache.current.get(cacheKey);
    },
    [chartSymbol],
  );

  // Track current price changes and update local order types
  const prevCurrentPriceRef = React.useRef(null);

  React.useEffect(() => {
    const currentPrice =
      chartState.data && chartState.data.length > 0
        ? chartState.data[chartState.data.length - 1]?.close
        : null;

    const prevCurrentPrice = prevCurrentPriceRef.current;

    // Only update if current price has actually changed and we have orders
    if (
      currentPrice !== null &&
      prevCurrentPrice !== null &&
      currentPrice !== prevCurrentPrice &&
      orders &&
      orders.length > 0
    ) {
      // Check each local order and update order type if price crossed
      orders.forEach((order) => {
        if (order.state === "local") {
          const oldOrderType = order.orderType;

          if (
            shouldOrderTypeCrossover(
              order.side,
              order.price,
              prevCurrentPrice,
              currentPrice,
            )
          ) {
            const newOrderType = determineOrderType(
              order.side,
              order.price,
              currentPrice,
            );

            if (newOrderType !== oldOrderType) {
              // Update the order type in the store
              updateOrder(chartSymbol, order.id, { orderType: newOrderType });
            }
          }
        }
      });
    }

    // Update the ref for next comparison
    prevCurrentPriceRef.current = currentPrice;
  }, [chartState.data, orders, chartSymbol, updateOrder]);

  // Handle order creation on chart click
  const handleChartClick = async (moreProps, e) => {
    // Check for modifier keys first to reduce logging
    const { shiftKey, ctrlKey, metaKey } = e;

    // Only process modifier keys if one-click trading is enabled
    if ((shiftKey || ctrlKey || metaKey) && oneClickTradingRef.current) {
      // Check if the pressed key is armed (was released since last order)
      // User must release the key between orders to prevent accidental multiple orders
      // This check is only active when modifierKeyDisarming setting is enabled
      if (modifierKeyDisarmingRef.current) {
        const isShiftArmed = shiftKey && shiftArmed;
        const isCtrlArmed = ctrlKey && ctrlArmed;
        const isMetaArmed = metaKey && metaArmed;

        // For sell orders (Ctrl/Cmd), check the specific key that's pressed
        const isSellKeyArmed = isCtrlArmed || isMetaArmed;
        const isArmed = isShiftArmed || isSellKeyArmed;

        if (!isArmed) {
          // Key must be released before next order - show blocked message
          // Determine which key to show in the message (use Mac-friendly names)
          if (shiftKey && !shiftArmed) {
            setBlockedKey("Shift");
          } else if (metaKey && !metaArmed) {
            setBlockedKey("Cmd");
          } else if (ctrlKey && !ctrlArmed) {
            setBlockedKey("Ctrl");
          }
          return;
        }
      }
      // Calculate price from mouse Y position
      let price = null;

      if (moreProps.mouseXY && moreProps.chartConfig) {
        const mouseY = moreProps.mouseXY[1]; // Y coordinate of mouse
        const { yScale } = moreProps.chartConfig;

        if (yScale && typeof yScale.invert === "function") {
          price = yScale.invert(mouseY); // Convert Y coordinate to price
        }
      }

      // Fallback to currentItem close if mouse position fails
      if (!price) {
        price = moreProps.currentItem?.close;
      }

      if (price && chartSymbol) {
        // Disarm the pressed modifier key immediately to prevent multiple orders
        // User must release and re-press to create another order
        // Only disarm if the modifierKeyDisarming setting is enabled
        if (modifierKeyDisarmingRef.current) {
          if (shiftKey) disarmKey("Shift");
          if (ctrlKey) disarmKey("Control");
          if (metaKey) disarmKey("Meta");
        }

        // Determine order side based on modifier key
        const side = shiftKey ? "buy" : "sell"; // Shift = buy, Ctrl/Cmd = sell

        // Get default quantity for the symbol
        const quantity = getAdjustedQuantity(chartSymbol) || 100;

        // Get current price from chart data for order type determination
        const currentPrice =
          chartState.data && chartState.data.length > 0
            ? chartState.data[chartState.data.length - 1]?.close
            : null;

        // Check if router is connected for direct submission
        const isRouterConnected = router.state === "online";

        // Read OTO config for this chart's symbol (non-reactively to avoid stale hook closure)
        const otoConfig = useOrderDefaultsStore
          .getState()
          .getOtoConfig(chartSymbol);

        // Helper: attach OTO legs to a local order in the store
        const attachOtoToLocalOrder = (localOrder) => {
          if (!otoConfig.enabled || !localOrder) return;
          const legs = buildOtoLegs({
            mainOrderId: localOrder.id,
            symbol: localOrder.symbol,
            side: localOrder.side,
            quantity: localOrder.quantity,
            anchorPrice: localOrder.price,
            lossTicks: otoConfig.lossTicks,
            profitTicks: otoConfig.profitTicks,
          });
          if (legs.length) {
            useOrderStore
              .getState()
              .updateOrder(localOrder.symbol, localOrder.id, { oto: legs });
          }
        };

        if (isRouterConnected) {
          // Router is connected - submit order directly
          try {
            // Determine order type based on price vs current market price
            const orderType = determineOrderType(side, price, currentPrice);

            // Create order object for router submission
            const orderData = {
              id: `${chartSymbol}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              symbol: chartSymbol,
              side: side,
              quantity: quantity,
              price: price,
              orderType: orderType,
            };

            // Attach OTO bracket when armed
            if (otoConfig.enabled) {
              const legs = buildOtoLegs({
                mainOrderId: orderData.id,
                symbol: chartSymbol,
                side,
                quantity,
                anchorPrice: price,
                lossTicks: otoConfig.lossTicks,
                profitTicks: otoConfig.profitTicks,
              });
              if (legs.length) orderData.oto = legs;
            }

            // Confirm before sending to broker
            const confirmed = await confirmNewOrder(orderData);
            if (!confirmed) return;

            // Submit directly to router
            const success = await submitOrderToRouter(
              orderData,
              () => {
                // Router login required — fallback to local order
                const localOrder = createOrder(
                  chartSymbol,
                  side,
                  price,
                  quantity,
                  currentPrice,
                );
                attachOtoToLocalOrder(localOrder);
              },
              () => {
                /* success */
              },
              (error) => {
                console.error("❌ Failed to submit order to router:", error);
                globalToast.showError(
                  error.message || String(error),
                  "Não foi possível enviar agora",
                );
              },
            );

            if (!success) {
              // Router submission failed — fallback to local order. silent: the
              // submit onError above already notified; avoid a 2nd toast (#351 review).
              const localOrder = createOrder(
                chartSymbol,
                side,
                price,
                quantity,
                currentPrice,
                { silent: true },
              );
              attachOtoToLocalOrder(localOrder);
            }
          } catch (error) {
            console.error("❌ Error during direct order submission:", error);
            // Fallback to local order creation (silent — onError already notified).
            const localOrder = createOrder(
              chartSymbol,
              side,
              price,
              quantity,
              currentPrice,
              { silent: true },
            );
            attachOtoToLocalOrder(localOrder);
          }
        } else {
          // Router not connected - create local order (no confirmation for local drafts)
          const localOrder = createOrder(
            chartSymbol,
            side,
            price,
            quantity,
            currentPrice,
          );
          attachOtoToLocalOrder(localOrder);
        }
      } else {
        console.warn("❌ Could not extract price or symbol for order creation");
      }
    } else if (shiftKey || ctrlKey || metaKey) {
      // Modifier keys pressed but one-click trading is disabled
    } else {
      // Only log normal clicks occasionally to reduce spam
      if (Math.random() < 0.1) {
        // 10% of clicks
      }
    }
  };

  // Calculate grid dimensions
  const gridWidth = width - CANVAS_MARGIN.left - CANVAS_MARGIN.right;
  const gridHeight = height - CANVAS_MARGIN.top - CANVAS_MARGIN.bottom;

  // Shared sizing for price/MA coordinate labels
  const PRICE_COORD_WIDTH = 70;
  const PRICE_COORD_HEIGHT = 22;

  // xFormat logic will be implemented when mouse coordinates are added back

  const priceFormatter = useMemo(() => {
    return (value) => {
      try {
        return formatPrice(chartSymbol, value);
      } catch {
        const num = Number(value);
        return Number.isFinite(num) ? num.toFixed(2) : String(value);
      }
    };
  }, [chartSymbol]);

  // Enhance chart configurations with order and position-aware yExtents for main chart
  const enhancedChartConfigs = useMemo(() => {
    return chartConfigs.map((config) => {
      if (config.type === "main") {
        // Filter orders for current symbol
        const symbolOrders = filterOrdersBySymbol(orders, chartSymbol);
        const hasOrders = symbolOrders.length > 0;
        const hasPosition =
          position && position.price && isFinite(position.price);

        // Only enhance if we have orders or position to include
        if (hasOrders || hasPosition) {
          // Wrap yExtentsCalculator to include orders and position
          const originalCalculator = config.yExtentsCalculator;
          const enhancedCalculator = (moreProps) => {
            const baseExtents = originalCalculator
              ? originalCalculator(moreProps)
              : [0, 1];
            let min = baseExtents[0];
            let max = baseExtents[1];

            // Include order prices
            if (hasOrders) {
              const orderExtents = getOrdersExtents(symbolOrders);
              if (orderExtents.min !== null && orderExtents.max !== null) {
                min = Math.min(min, orderExtents.min);
                max = Math.max(max, orderExtents.max);
              }
            }

            // Include position entry price
            if (hasPosition) {
              const posExtents = getPositionExtents(position);
              if (posExtents.min !== null && posExtents.max !== null) {
                min = Math.min(min, posExtents.min);
                max = Math.max(max, posExtents.max);
              }
            }

            return [min, max];
          };

          return { ...config, yExtentsCalculator: enhancedCalculator };
        }
      }
      return config;
    });
  }, [chartConfigs, orders, chartSymbol, position]);

  // Calculate baselines for compare mode from visible data
  // The baseline is the close price of the first visible bar (ChartIQ-style)
  // This must be before the guard clause to follow React's rules of hooks
  const compareBaselines = useMemo(() => {
    if (!isCompareMode || !chartState?.data || chartState.data.length === 0) {
      return { main: null, comparisons: {} };
    }

    // Get the first visible bar index from xExtents
    // xExtents[0] is the start index of visible data
    const xExtents = chartState.xExtents;
    const xAccessor = chartState.xAccessor;

    let firstVisibleIndex = 0;
    if (xExtents && xAccessor && chartState.data.length > 0) {
      // Find the first data point that's >= xExtents[0]
      for (let i = 0; i < chartState.data.length; i++) {
        const idx = xAccessor(chartState.data[i]);
        if (idx >= xExtents[0]) {
          firstVisibleIndex = i;
          break;
        }
      }
    }

    // Calculate main baseline
    const mainBaseline = getCompareBaseline(
      chartState.data,
      firstVisibleIndex,
      "close",
    );

    // Calculate comparison baselines
    const comparisonBaselinesMap = {};
    for (const { symbol } of comparisonSymbols || []) {
      const closeKey = `compare_${symbol}_close`;
      comparisonBaselinesMap[symbol] = getCompareBaseline(
        chartState.data,
        firstVisibleIndex,
        closeKey,
      );
    }

    return {
      main: mainBaseline,
      comparisons: comparisonBaselinesMap,
    };
  }, [
    isCompareMode,
    chartState?.data,
    chartState?.xExtents,
    chartState?.xAccessor,
    comparisonSymbols,
  ]);

  // Guard: Don't render if data is invalid or insufficient
  // ChartCanvas requires at least 2 datapoints to calculate scales
  if (!chartState?.data || chartState.data.length < 2) {
    return (
      <div
        ref={ref}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#888",
        }}
      >
        Carregando dados...
      </div>
    );
  }

  return (
    <div ref={ref} style={{ width: "100%", position: "relative" }}>
      <ChartCanvas
        key={buildChartKey({
          chartId,
          resetKey,
          renkoConfigHash,
          pointFigureConfigHash,
          isCompareMode,
        })}
        data-chart-id={chartId}
        height={height}
        width={width}
        ratio={ratio}
        padding={CANVAS_PADDING}
        margin={CANVAS_MARGIN}
        type={type}
        seriesName={chartState.chartSymbol || "Chart"}
        data={chartState.data}
        xScale={chartState.xScale}
        xAccessor={chartState.xAccessor}
        displayXAccessor={chartState.displayXAccessor}
        xExtents={chartState.xExtents}
        useCrossHairStyleCursor={shouldShowCrosshair}
        layoutSnapshot={layoutSnapshot}
        onVisibleBarsChange={handleChartCanvasVisibleBarsChange}
        onXExtentsChange={handleChartCanvasXExtentsChange}
        onLoadMore={handleLoadMore}
        perfMonitor={perfMonitor}
        panEvent={!isMeasureModeActive}
      >
        {enhancedChartConfigs.map((config, idx) => {
          const isLast = idx === orderedCharts.length - 1;

          if (config.type === "main") {
            // NOTE: Previously used orderKey in Chart's key to force re-render on order changes.
            // This caused Chart to REMOUNT (not just re-render), which reset all component state
            // including fresh moreProps from Y-axis zoom, causing coordinate bugs.
            // React will re-render children when props change - no key change needed.

            return (
              <Chart
                key={`${config.id}`}
                id={config.id}
                height={config.height}
                origin={config.origin} // Keep original origin for Chart component
                yExtents={config.yExtents}
                yExtentsCalculator={config.yExtentsCalculator}
                yPan={config.yPan}
                padding={config.padding}
                chartType={config.chartType}
                chartSymbol={config.chartSymbol}
                onContextMenu={handleChartAreaContextMenu}
              >
                <XAxisBase
                  themeColors={themeColors}
                  showGrid={showGrid}
                  gridHeight={gridHeight}
                  showAxis={isLast}
                />
                <YAxisBase
                  themeColors={themeColors}
                  ticks={8}
                  height={gridHeight}
                  inverted
                  showGrid={showGrid}
                  gridWidth={gridWidth}
                  chartSymbol={chartSymbol}
                  isPriceAxis={!isCompareMode}
                  isCompareMode={isCompareMode}
                  mainBaseline={compareBaselines.main}
                />
                <SeriesRenderer
                  chartType={config.chartType}
                  themeColors={themeColors}
                />

                {/* Compare mode: Render comparison symbol lines */}
                {/* Main symbol stays as OHLC candlesticks - only comparison symbols are line series */}
                {/* Comparison prices are normalized to main symbol's price scale */}
                {isCompareMode &&
                  comparisonSymbols.map((comparison) => {
                    const compBaseline =
                      compareBaselines.comparisons[comparison.symbol];
                    const mainBase = compareBaselines.main;

                    return (
                      <LineSeries
                        key={`compare-${comparison.symbol}`}
                        yAccessor={(d) => {
                          const closePrice =
                            d[`compare_${comparison.symbol}_close`];
                          // If no data or baselines, return null
                          if (closePrice == null || !compBaseline || !mainBase)
                            return null;
                          // Transform comparison price to main symbol's price scale
                          // This makes percentage changes align visually
                          const compPercent = priceToPercent(
                            closePrice,
                            compBaseline,
                          );
                          if (compPercent == null) return null;
                          // Convert percentage back to a price on main symbol's scale
                          const normalizedPrice =
                            mainBase * (1 + compPercent / 100);
                          return normalizedPrice;
                        }}
                        stroke={{
                          color: comparison.color,
                          opacity: 1,
                          width: 2,
                          dash: "Solid",
                        }}
                      />
                    );
                  })}

                {/* Compare mode: Y-axis labels for comparison symbols */}
                {isCompareMode &&
                  showIndicatorLabels &&
                  comparisonSymbols.map((comparison) => {
                    const compBaseline =
                      compareBaselines.comparisons[comparison.symbol];
                    const mainBase = compareBaselines.main;
                    const dataArr = chartState.data;

                    if (!Array.isArray(dataArr) || dataArr.length === 0)
                      return null;
                    if (!compBaseline || !mainBase) return null;

                    const last = dataArr[dataArr.length - 1];
                    const closePrice =
                      last[`compare_${comparison.symbol}_close`];
                    if (closePrice == null) return null;

                    // Calculate normalized price for Y-axis position (same as LineSeries)
                    const compPercent = priceToPercent(
                      closePrice,
                      compBaseline,
                    );
                    if (compPercent == null) return null;
                    const normalizedPrice = mainBase * (1 + compPercent / 100);

                    // Format the actual price for display (not the normalized value)
                    const displayValue = formatPrice(
                      comparison.symbol,
                      closePrice,
                    );

                    return (
                      <PriceCoordinate
                        key={`compare-label-${comparison.symbol}`}
                        price={normalizedPrice}
                        displayFormat={() => displayValue}
                        orient="right"
                        at="right"
                        rectWidth={PRICE_COORD_WIDTH}
                        rectHeight={PRICE_COORD_HEIGHT}
                        fill={comparison.color}
                        textFill="#ffffff"
                        opacity={1}
                        hideLine={true}
                        arrowWidth={6}
                        fontSize={12}
                        dx={5}
                      />
                    );
                  })}

                {/* Capture last close price info (will render after MA coordinates for z-order) */}
                {(() => {
                  if (!chartState.data || chartState.data.length === 0)
                    return null;
                  const lastDataPoint =
                    chartState.data[chartState.data.length - 1];
                  const lastClosePrice = lastDataPoint?.close;
                  const previousDataPoint =
                    chartState.data.length > 1
                      ? chartState.data[chartState.data.length - 2]
                      : null;
                  const previousClosePrice = previousDataPoint?.close;
                  const isPriceUp = previousClosePrice
                    ? lastClosePrice > previousClosePrice
                    : true;
                  const getPriceCoordinateColors = (chartType, isUp) => {
                    switch (chartType) {
                      case "candle":
                        return isUp
                          ? { fill: "#8cc176", textFill: "#ffffff" }
                          : { fill: "#c53717", textFill: "#ffffff" };
                      case "hollowcandle":
                        return isUp
                          ? { fill: "#4caf50", textFill: "#ffffff" }
                          : { fill: "#c53717", textFill: "#ffffff" };
                      case "heikinashi":
                        return isUp
                          ? { fill: "#A6E97A", textFill: "#000000" }
                          : { fill: "#FF4B2B", textFill: "#ffffff" };
                      case "colorbar":
                        return isUp
                          ? { fill: "#8cc176", textFill: "#ffffff" }
                          : { fill: "#c53717", textFill: "#ffffff" };
                      case "rangebars":
                        return isUp
                          ? { fill: "#A6E97A", textFill: "#000000" }
                          : { fill: "#FF4B2B", textFill: "#ffffff" };
                      case "kagi":
                        return isUp
                          ? { fill: "#75FB4C", textFill: "#000000" }
                          : { fill: "#EA3323", textFill: "#ffffff" };
                      default:
                        return isUp
                          ? { fill: "#2563eb", textFill: "#ffffff" }
                          : { fill: "#dc2626", textFill: "#ffffff" };
                    }
                  };
                  const formatPriceLocal = (p) => {
                    try {
                      return formatPrice(chartSymbol, p);
                    } catch {
                      const tickSize =
                        marketdata && marketdata.getMinInc
                          ? marketdata.getMinInc(chartSymbol) || 0.01
                          : 0.01;
                      const decimals =
                        tickSize >= 1
                          ? 0
                          : (tickSize.toString().split(".")[1] || "").length;
                      return Number(p).toFixed(decimals);
                    }
                  };
                  // Store on config for later render
                  config.__lastClosePriceMeta = {
                    lastClosePrice,
                    colors: getPriceCoordinateColors(
                      config.chartType,
                      isPriceUp,
                    ),
                    formatPrice: formatPriceLocal,
                  };
                  return null;
                })()}

                <InteractiveLayer
                  selectorEnabled={true}
                  chartSectionId={config.id}
                  chartState={chartState}
                  chartSymbol={chartSymbol}
                  onInteractiveApiReady={handleMainInteractiveApiReady}
                  onInteractiveContextMenu={handleInteractiveContextMenu}
                  onOpenEditModal={handleOpenEditModal}
                  onDeleteInteractive={handleInteractiveRemove}
                  timeDisplayFormat={(value) =>
                    formatDateByPeriodicity(value, periodicity)
                  }
                  isLastSection={isLast}
                  priceDisplayFormat={priceFormatter}
                />

                {/* Order Interactive - renders order lines on main chart with new architecture */}
                <InteractiveOrderIntegration
                  symbol={chartSymbol}
                  enabled={true}
                  onDragStateChange={handleOrderDragStateChange}
                  currentPrice={
                    chartState.data && chartState.data.length > 0
                      ? chartState.data[chartState.data.length - 1]?.close
                      : null
                  }
                />

                {config.indicators &&
                  config.indicators.map((indicator) => (
                    <React.Fragment key={`${indicator.id}-group`}>
                      <IndicatorRenderer
                        key={`${indicator.id}-${indicator.renderKey || indicator.key}-renderer`}
                        indicator={indicator}
                        chartType={config.chartType}
                        onContextMenu={(moreProps, e) =>
                          handleIndicatorSeriesContextMenu(
                            moreProps,
                            e,
                            indicator,
                          )
                        }
                        onHover={() => handleIndicatorHover(indicator)}
                        onUnHover={() => handleIndicatorUnHover(indicator.id)}
                      />

                      {/* Issue #161 — Ichimoku future projection overlay.
                      IndicatorRenderer only renders registryEntry.series (per Round 3 review),
                      so the overlay needs explicit mount here. Gated by feature flag. */}
                      {indicator.key === "ichimoku" &&
                        showIchimokuFutureProjection && (
                          <IchimokuFutureCloudOverlay
                            key={`${indicator.id}-future-overlay`}
                            indicatorConfig={{
                              conversionPeriod:
                                indicator.config?.conversionPeriod ?? 9,
                              basePeriod: indicator.config?.basePeriod ?? 26,
                              spanBPeriod: indicator.config?.spanBPeriod ?? 52,
                              displacement:
                                indicator.config?.displacement ?? 26,
                            }}
                            bullishFill={
                              indicator.appearanceConfig?.spanA?.color ??
                              "#4CAF50"
                            }
                            bearishFill={
                              indicator.appearanceConfig?.spanB?.color ??
                              "#F44336"
                            }
                            dashed
                            periodicity={periodicity}
                          />
                        )}
                      {showIndicatorLabels &&
                        indicator.renderKey &&
                        (() => {
                          try {
                            const yAccessor = (d) => d[indicator.renderKey];
                            const dataArr = chartState.data;
                            if (!Array.isArray(dataArr) || dataArr.length === 0)
                              return null;
                            const last = dataArr[dataArr.length - 1];
                            const rawVal = yAccessor(last);
                            if (rawVal == null) return null;

                            // Get registry entry to check for multi-line config (e.g., Pivot Points)
                            const registryEntry =
                              indicatorRegistry[
                                Object.keys(indicatorRegistry).find(
                                  (cat) =>
                                    indicatorRegistry[cat].indicators &&
                                    indicatorRegistry[cat].indicators[
                                      indicator.key
                                    ],
                                )
                              ]?.indicators?.[indicator.key];

                            // Check if this is a multi-line indicator on main chart (Pivot Points)
                            if (
                              typeof rawVal === "object" &&
                              !Array.isArray(rawVal) &&
                              registryEntry?.labelConfig?.lines
                            ) {
                              // Multi-line indicator on main chart (Pivot Points, etc.)
                              return registryEntry.labelConfig.lines.map(
                                (lineConfig, idx) => {
                                  const lineValue = rawVal[lineConfig.key];
                                  if (lineValue == null) return null;

                                  // Extract color from appearance config using colorPath
                                  const pathParts =
                                    lineConfig.colorPath.split(".");
                                  let color = indicator.appearanceConfig;
                                  for (const part of pathParts) {
                                    color = color?.[part];
                                  }
                                  const strokeColor =
                                    (typeof color === "string"
                                      ? color
                                      : color?.color) || "#9C27B0";

                                  // Use price formatting for main chart overlays with isPriceAxis: true
                                  const displayValue = formatPrice(
                                    chartSymbol,
                                    lineValue,
                                  );

                                  return (
                                    <PriceCoordinate
                                      key={`${indicator.id}-${lineConfig.key}-${idx}`}
                                      price={lineValue}
                                      displayFormat={() => displayValue}
                                      orient="right"
                                      at="right"
                                      rectWidth={PRICE_COORD_WIDTH}
                                      rectHeight={PRICE_COORD_HEIGHT}
                                      fill={strokeColor}
                                      textFill="#ffffff"
                                      opacity={1}
                                      hideLine={true}
                                      arrowWidth={6}
                                      fontSize={12}
                                      dx={5}
                                    />
                                  );
                                },
                              );
                            }

                            // Single-value indicator - reject objects without labelConfig
                            if (
                              typeof rawVal === "object" &&
                              !Array.isArray(rawVal)
                            )
                              return null;

                            // Main chart overlays MUST use price formatting (not indicator decimals)
                            const adjustedPrice = formatPrice(
                              chartSymbol,
                              rawVal,
                            );
                            const strokeColor =
                              indicator.appearanceConfig?.stroke?.color ||
                              indicator.appearanceConfig?.stroke ||
                              "#FF9800";

                            return (
                              <PriceCoordinate
                                key={`${indicator.id}-${indicator.renderKey || indicator.key}-price`}
                                price={rawVal}
                                displayFormat={() => adjustedPrice}
                                orient="right"
                                at="right"
                                rectWidth={PRICE_COORD_WIDTH}
                                rectHeight={PRICE_COORD_HEIGHT}
                                fill={strokeColor}
                                textFill="#ffffff"
                                opacity={1}
                                hideLine={true}
                                arrowWidth={6}
                                fontSize={12}
                                dx={5}
                              />
                            );
                          } catch {
                            return null;
                          }
                        })()}
                    </React.Fragment>
                  ))}
                {shouldShowCrosshair && (
                  <CrossHair
                    themeColors={themeColors}
                    strokeWidth={crosshairWidth}
                  />
                )}
                {/* Order hover indicator for one-click trading */}
                {oneClickTrading && (
                  <OrderHoverIndicator
                    symbol={chartSymbol}
                    yAccessor={(d) => d.close}
                    enabled={true}
                    oneClickTrading={oneClickTrading}
                    shiftArmed={shiftArmed}
                    ctrlArmed={ctrlArmed}
                    metaArmed={metaArmed}
                    modifierKeyDisarming={modifierKeyDisarming}
                    blockedKey={blockedKey}
                  />
                )}
                {/* Measure mode overlay (Issue #101) */}
                {isMeasureModeActive && (
                  <ClickCallback
                    disablePan={true}
                    onMouseDown={handleMeasureMouseDown}
                    onMouseMove={handleMeasureMouseMove}
                  />
                )}
                <MeasurementOverlay
                  enabled={isMeasureModeActive && measureStartPoint !== null}
                  startPoint={measureStartPoint}
                  endPoint={measureEndPoint}
                  formatPrice={formatMeasurePrice}
                />
                {/* Render last close PriceCoordinate after MA coordinates so it appears on top */}
                {config.__lastClosePriceMeta &&
                  config.__lastClosePriceMeta.lastClosePrice &&
                  chartSymbol && (
                    <PriceCoordinate
                      key="last-close-price"
                      price={config.__lastClosePriceMeta.lastClosePrice}
                      displayFormat={config.__lastClosePriceMeta.formatPrice}
                      orient="right"
                      at="right"
                      rectWidth={PRICE_COORD_WIDTH}
                      rectHeight={PRICE_COORD_HEIGHT}
                      fill={config.__lastClosePriceMeta.colors.fill}
                      textFill={config.__lastClosePriceMeta.colors.textFill}
                      opacity={0.95}
                      lineStroke={config.__lastClosePriceMeta.colors.fill}
                      arrowWidth={6}
                      fontSize={12}
                      fontWeight="bold"
                      dx={5}
                      alwaysShow={true}
                    />
                  )}
                {shouldShowCrosshair && (
                  <>
                    {isLast && (
                      <MouseCoordinateX
                        displayFormat={(date) =>
                          formatDateByPeriodicity(date, periodicity)
                        }
                        orient="bottom"
                        at="bottom"
                        rectWidth={100}
                        rectHeight={24}
                        fill={themeColors.mouseCoordinateFill}
                        textFill={themeColors.mouseCoordinateText}
                        opacity={0.9}
                        arrowWidth={6}
                        fontSize={12}
                        fontWeight="bold"
                        dy={5}
                      />
                    )}
                    <MouseCoordinateY
                      displayFormat={getDisplayFormat(config)}
                      orient="right"
                      at="right"
                      rectWidth={80}
                      rectHeight={24}
                      fill={themeColors.mouseCoordinateFill}
                      textFill={themeColors.mouseCoordinateText}
                      opacity={0.9}
                      arrowWidth={6}
                      fontSize={12}
                      fontWeight="bold"
                      dx={5}
                    />
                  </>
                )}

                {/* {isLast && ( */}
                <ZoomButtons
                  heightFromBase={55}
                  fill={themeColors.background}
                  fillOpacity={0.8}
                  stroke={themeColors.border}
                  textFill={themeColors.text}
                  zoomMultiplier={1.5}
                  onReset={handleReset}
                  showZoomControls={
                    zoomButtonsAlwaysVisible || isPointerInsideChartPanel
                  }
                />
                {/* )} */}

                <ChartTooltips
                  config={config}
                  indicatorRegistry={indicatorRegistry}
                  themeColors={themeColors}
                  xFormat={timeFormat("%Y-%m-%d")}
                  periodicity={periodicity}
                  chartId={chartId}
                  chartSymbol={chartSymbol}
                  sectionIndex={idx}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < chartConfigs.length - 1}
                  onMoveUp={() => moveSectionUp && moveSectionUp(chartId, idx)}
                  onMoveDown={() =>
                    moveSectionDown && moveSectionDown(chartId, idx)
                  }
                  indicatorMenuRef={indicatorMenuRef}
                  isLastSection={isLast}
                  onStartResize={handleStartResize}
                  onResize={handleResize}
                  onEndResize={handleEndResize}
                  onResetSize={() => {}}
                />
                <ClickCallback
                  onClick={handleChartClick}
                  onMouseMove={handleInfoHover}
                  disablePan={false}
                  data-evo-id="chartrenderer-btn-pan-toggle"
                />
              </Chart>
            );
          }

          return (
            <Chart
              key={config.id}
              id={config.id}
              height={config.height}
              origin={config.origin} // Keep original origin for Chart component
              yExtents={config.yExtents}
              yExtentsCalculator={config.yExtentsCalculator}
              yPan={config.yPan}
              padding={{ top: 25, bottom: 10 }}
              onContextMenu={handleChartAreaContextMenu}
            >
              <XAxisBase
                themeColors={themeColors}
                showGrid={showGrid}
                gridHeight={config.height}
                showAxis={isLast}
              />
              <YAxisBase
                themeColors={themeColors}
                ticks={5}
                height={config.height}
                showGrid={showGrid}
                gridWidth={gridWidth}
                chartSymbol={chartSymbol}
                isPriceAxis={
                  config.indicator
                    ? isPriceAxisIndicator(config.indicator)
                    : false
                }
                decimalPlaces={config.indicator?.decimalPlaces ?? 0}
                isVolumeAxis={
                  config.indicator?.key === "volume" ||
                  config.indicators?.some((ind) => ind.key === "volume") ||
                  config.indicator?.isVolumeAxis === true ||
                  config.indicators?.some((ind) => ind.isVolumeAxis === true)
                }
              />
              {/* PHASE 3: Render all indicators in this panel (primary + overlays) */}
              {config.indicators
                ? // New structure with indicators array (series-as-source support)
                  config.indicators.map((indicator) => (
                    <React.Fragment key={`${indicator.id}-group`}>
                      <IndicatorRenderer
                        key={`${indicator.id}-${indicator.renderKey || indicator.key}-renderer`}
                        indicator={indicator}
                        chartType={config.chartType}
                        onContextMenu={(moreProps, e) =>
                          handleIndicatorSeriesContextMenu(
                            moreProps,
                            e,
                            indicator,
                          )
                        }
                        onHover={() => handleIndicatorHover(indicator)}
                        onUnHover={() => handleIndicatorUnHover(indicator.id)}
                      />
                      {showIndicatorLabels &&
                        indicator.renderKey &&
                        (() => {
                          try {
                            const yAccessor = (d) => d[indicator.renderKey];
                            const dataArr = chartState.data;
                            if (!Array.isArray(dataArr) || dataArr.length === 0)
                              return null;
                            const last = dataArr[dataArr.length - 1];
                            const rawVal = yAccessor(last);
                            if (rawVal == null) return null;

                            // Get registry entry to check for multi-line config
                            const registryEntry =
                              indicatorRegistry[
                                Object.keys(indicatorRegistry).find(
                                  (cat) =>
                                    indicatorRegistry[cat].indicators &&
                                    indicatorRegistry[cat].indicators[
                                      indicator.key
                                    ],
                                )
                              ]?.indicators?.[indicator.key];

                            // Check if this is a multi-line indicator
                            if (
                              typeof rawVal === "object" &&
                              !Array.isArray(rawVal) &&
                              registryEntry?.labelConfig?.lines
                            ) {
                              // Multi-line indicator (Stochastic, MACD, etc.)
                              return registryEntry.labelConfig.lines.map(
                                (lineConfig, idx) => {
                                  const lineValue = rawVal[lineConfig.key];
                                  if (lineValue == null) return null;

                                  // Extract color from appearance config using colorPath
                                  const pathParts =
                                    lineConfig.colorPath.split(".");
                                  let color = indicator.appearanceConfig;
                                  for (const part of pathParts) {
                                    color = color?.[part];
                                  }
                                  const strokeColor =
                                    (typeof color === "string"
                                      ? color
                                      : color?.color) || "#2196F3";

                                  const decimals = indicator.decimalPlaces ?? 2;
                                  const displayValue =
                                    Number(lineValue).toFixed(decimals);

                                  return (
                                    <PriceCoordinate
                                      key={`${indicator.id}-${lineConfig.key}-${idx}`}
                                      price={lineValue}
                                      displayFormat={() => displayValue}
                                      orient="right"
                                      at="right"
                                      rectWidth={PRICE_COORD_WIDTH}
                                      rectHeight={PRICE_COORD_HEIGHT}
                                      fill={strokeColor}
                                      textFill="#ffffff"
                                      opacity={1}
                                      hideLine={true}
                                      arrowWidth={6}
                                      fontSize={12}
                                      dx={5}
                                    />
                                  );
                                },
                              );
                            }

                            // Special case: Volume indicator with {volume, direction} structure
                            if (
                              typeof rawVal === "object" &&
                              !Array.isArray(rawVal) &&
                              rawVal.volume != null &&
                              rawVal.direction != null
                            ) {
                              const volumeValue = rawVal.volume;
                              const direction = rawVal.direction;
                              const upColor =
                                indicator.appearanceConfig?.upColor?.color ||
                                "#6BA583";
                              const downColor =
                                indicator.appearanceConfig?.downColor?.color ||
                                "#FF0000";
                              const strokeColor =
                                direction === "up" ? upColor : downColor;

                              // Format volume with K/M/B suffixes
                              const formatVolume = (val) => {
                                if (val >= 1000000000)
                                  return (val / 1000000000).toFixed(2) + "B";
                                if (val >= 1000000)
                                  return (val / 1000000).toFixed(2) + "M";
                                if (val >= 1000)
                                  return (val / 1000).toFixed(2) + "K";
                                return val.toFixed(0);
                              };
                              const displayValue = formatVolume(volumeValue);

                              return (
                                <PriceCoordinate
                                  price={volumeValue}
                                  displayFormat={() => displayValue}
                                  orient="right"
                                  at="right"
                                  rectWidth={PRICE_COORD_WIDTH}
                                  rectHeight={PRICE_COORD_HEIGHT}
                                  fill={strokeColor}
                                  textFill="#ffffff"
                                  opacity={1}
                                  hideLine={true}
                                  arrowWidth={6}
                                  fontSize={12}
                                  dx={5}
                                />
                              );
                            }

                            // Single-line indicator (RSI, ATR, etc.)
                            if (
                              typeof rawVal === "object" &&
                              !Array.isArray(rawVal)
                            )
                              return null;
                            let displayValue;
                            if (indicator.isVolumeAxis) {
                              const absVal = Math.abs(rawVal);
                              if (absVal >= 1000000000)
                                displayValue =
                                  (rawVal / 1000000000).toFixed(2) + "B";
                              else if (absVal >= 1000000)
                                displayValue =
                                  (rawVal / 1000000).toFixed(2) + "M";
                              else if (absVal >= 1000)
                                displayValue = (rawVal / 1000).toFixed(2) + "K";
                              else displayValue = Number(rawVal).toFixed(0);
                            } else {
                              const decimals = indicator.decimalPlaces ?? 2;
                              displayValue = Number(rawVal).toFixed(decimals);
                            }
                            const strokeColor =
                              indicator.appearanceConfig?.stroke?.color ||
                              indicator.appearanceConfig?.stroke ||
                              "#2196F3";

                            return (
                              <PriceCoordinate
                                price={rawVal}
                                displayFormat={() => displayValue}
                                orient="right"
                                at="right"
                                rectWidth={PRICE_COORD_WIDTH}
                                rectHeight={PRICE_COORD_HEIGHT}
                                fill={strokeColor}
                                textFill="#ffffff"
                                opacity={1}
                                hideLine={true}
                                arrowWidth={6}
                                fontSize={12}
                                dx={5}
                              />
                            );
                          } catch {
                            return null;
                          }
                        })()}
                    </React.Fragment>
                  ))
                : // Fallback to single indicator for backward compatibility
                  config.indicator &&
                  [
                    <IndicatorRenderer
                      key={`${config.indicator.id}-${config.indicator.renderKey || config.indicator.key}-renderer`}
                      indicator={config.indicator}
                      chartType={config.chartType}
                      onContextMenu={(moreProps, e) =>
                        handleIndicatorSeriesContextMenu(
                          moreProps,
                          e,
                          config.indicator,
                        )
                      }
                      onHover={() => handleIndicatorHover(config.indicator)}
                      onUnHover={() =>
                        handleIndicatorUnHover(config.indicator.id)
                      }
                    />,
                    showIndicatorLabels &&
                      config.indicator.renderKey &&
                      (() => {
                        try {
                          const indicator = config.indicator;
                          const yAccessor = (d) => d[indicator.renderKey];
                          const dataArr = chartState.data;
                          if (!Array.isArray(dataArr) || dataArr.length === 0)
                            return null;
                          const last = dataArr[dataArr.length - 1];
                          const rawVal = yAccessor(last);
                          if (rawVal == null) return null;

                          // Get registry entry to check for multi-line config
                          const registryEntry =
                            indicatorRegistry[
                              Object.keys(indicatorRegistry).find(
                                (cat) =>
                                  indicatorRegistry[cat].indicators &&
                                  indicatorRegistry[cat].indicators[
                                    indicator.key
                                  ],
                              )
                            ]?.indicators?.[indicator.key];

                          // Check if this is a multi-line indicator
                          if (
                            typeof rawVal === "object" &&
                            !Array.isArray(rawVal) &&
                            registryEntry?.labelConfig?.lines
                          ) {
                            // Multi-line indicator (Stochastic, MACD, etc.)
                            return registryEntry.labelConfig.lines.map(
                              (lineConfig, idx) => {
                                const lineValue = rawVal[lineConfig.key];
                                if (lineValue == null) return null;

                                // Extract color from appearance config using colorPath
                                const pathParts =
                                  lineConfig.colorPath.split(".");
                                let color = indicator.appearanceConfig;
                                for (const part of pathParts) {
                                  color = color?.[part];
                                }
                                const strokeColor =
                                  (typeof color === "string"
                                    ? color
                                    : color?.color) || "#2196F3";

                                const decimals = indicator.decimalPlaces ?? 2;
                                const displayValue =
                                  Number(lineValue).toFixed(decimals);

                                return (
                                  <PriceCoordinate
                                    key={`${indicator.id}-${lineConfig.key}-${idx}`}
                                    price={lineValue}
                                    displayFormat={() => displayValue}
                                    orient="right"
                                    at="right"
                                    rectWidth={PRICE_COORD_WIDTH}
                                    rectHeight={PRICE_COORD_HEIGHT}
                                    fill={strokeColor}
                                    textFill="#ffffff"
                                    opacity={1}
                                    hideLine={true}
                                    arrowWidth={6}
                                    fontSize={12}
                                    dx={5}
                                  />
                                );
                              },
                            );
                          }

                          // Special case: Volume indicator with {volume, direction} structure
                          if (
                            typeof rawVal === "object" &&
                            !Array.isArray(rawVal) &&
                            rawVal.volume != null &&
                            rawVal.direction != null
                          ) {
                            const volumeValue = rawVal.volume;
                            const direction = rawVal.direction;
                            const upColor =
                              indicator.appearanceConfig?.upColor?.color ||
                              "#6BA583";
                            const downColor =
                              indicator.appearanceConfig?.downColor?.color ||
                              "#FF0000";
                            const strokeColor =
                              direction === "up" ? upColor : downColor;

                            // Format volume with K/M/B suffixes
                            const formatVolume = (val) => {
                              if (val >= 1000000000)
                                return (val / 1000000000).toFixed(2) + "B";
                              if (val >= 1000000)
                                return (val / 1000000).toFixed(2) + "M";
                              if (val >= 1000)
                                return (val / 1000).toFixed(2) + "K";
                              return val.toFixed(0);
                            };
                            const displayValue = formatVolume(volumeValue);

                            return (
                              <PriceCoordinate
                                price={volumeValue}
                                displayFormat={() => displayValue}
                                orient="right"
                                at="right"
                                rectWidth={PRICE_COORD_WIDTH}
                                rectHeight={PRICE_COORD_HEIGHT}
                                fill={strokeColor}
                                textFill="#ffffff"
                                opacity={1}
                                hideLine={true}
                                arrowWidth={6}
                                fontSize={12}
                                dx={5}
                              />
                            );
                          }

                          // Single-line indicator (RSI, ATR, etc.)
                          if (
                            typeof rawVal === "object" &&
                            !Array.isArray(rawVal)
                          )
                            return null;
                          let displayValue;
                          if (indicator.isVolumeAxis) {
                            const absVal = Math.abs(rawVal);
                            if (absVal >= 1000000000)
                              displayValue =
                                (rawVal / 1000000000).toFixed(2) + "B";
                            else if (absVal >= 1000000)
                              displayValue =
                                (rawVal / 1000000).toFixed(2) + "M";
                            else if (absVal >= 1000)
                              displayValue = (rawVal / 1000).toFixed(2) + "K";
                            else displayValue = Number(rawVal).toFixed(0);
                          } else {
                            const decimals = indicator.decimalPlaces ?? 2;
                            displayValue = Number(rawVal).toFixed(decimals);
                          }
                          const strokeColor =
                            indicator.appearanceConfig?.stroke?.color ||
                            indicator.appearanceConfig?.stroke ||
                            "#2196F3";

                          return (
                            <PriceCoordinate
                              price={rawVal}
                              displayFormat={() => displayValue}
                              orient="right"
                              at="right"
                              rectWidth={PRICE_COORD_WIDTH}
                              rectHeight={PRICE_COORD_HEIGHT}
                              fill={strokeColor}
                              textFill="#ffffff"
                              opacity={1}
                              hideLine={true}
                              arrowWidth={6}
                              fontSize={12}
                              dx={5}
                            />
                          );
                        } catch {
                          return null;
                        }
                      })(),
                  ].filter(Boolean)}

              <InteractiveLayer
                selectorEnabled={true}
                chartSectionId={config.id}
                chartState={chartState}
                chartSymbol={chartSymbol}
                onInteractiveContextMenu={handleInteractiveContextMenu}
                onOpenEditModal={handleOpenEditModal}
                onDeleteInteractive={handleInteractiveRemove}
                timeDisplayFormat={(value) =>
                  formatDateByPeriodicity(value, periodicity)
                }
                isLastSection={isLast}
                priceDisplayFormat={priceFormatter}
              />
              {shouldShowCrosshair && <CrossHair themeColors={themeColors} />}
              {shouldShowCrosshair && (
                <>
                  {isLast && (
                    <MouseCoordinateX
                      displayFormat={(date) =>
                        formatDateByPeriodicity(date, periodicity)
                      }
                      orient="bottom"
                      at="bottom"
                      rectWidth={100}
                      rectHeight={24}
                      fill="#1f2937"
                      textFill="#ffffff"
                      opacity={0.9}
                      arrowWidth={6}
                      fontSize={12}
                      fontWeight="bold"
                      dy={5}
                    />
                  )}
                  <MouseCoordinateY
                    displayFormat={getDisplayFormat(config)}
                    orient="right"
                    at="right"
                    rectWidth={80}
                    rectHeight={24}
                    fill="#1f2937"
                    textFill="#ffffff"
                    opacity={0.9}
                    arrowWidth={6}
                    fontSize={12}
                    fontWeight="bold"
                    dx={5}
                  />
                </>
              )}
              {/* {isLast && (
              <ZoomButtons
                heightFromBase={35}
                fill={themeColors.background}
                fillOpacity={0.8}
                stroke={themeColors.border}
                textFill={themeColors.text}
                zoomMultiplier={1.5}
                onReset={handleReset}
              />
            )} */}

              <ChartTooltips
                config={config}
                indicatorRegistry={indicatorRegistry}
                themeColors={themeColors}
                xFormat={timeFormat("%Y-%m-%d")}
                periodicity={periodicity}
                chartId={chartId}
                chartSymbol={chartSymbol}
                sectionIndex={idx}
                canMoveUp={idx > 0}
                canMoveDown={idx < chartConfigs.length - 1}
                onMoveUp={() => moveSectionUp && moveSectionUp(chartId, idx)}
                onMoveDown={() =>
                  moveSectionDown && moveSectionDown(chartId, idx)
                }
                indicatorMenuRef={indicatorMenuRef}
                isLastSection={isLast}
                onStartResize={handleStartResize}
                onResize={handleResize}
                onEndResize={handleEndResize}
                onResetSize={() => {}}
              />
            </Chart>
          );
        })}
      </ChartCanvas>

      {/* Bottom indicators - countdown, price change, renko (dynamically positioned) */}
      <ChartBottomIndicators
        chartId={chartId}
        chartData={chartState.data}
        periodicity={periodicity}
        chartType={chartConfigs[0]?.chartType}
        showCountdown={resolveVisibilityMode(barCountdownVisibilityMode)}
        showPriceChange={resolveVisibilityMode(priceChangeVisibilityMode)}
      />

      {/* Compare Legend - positioned at bottom left */}
      {isCompareMode && comparisonSymbols && comparisonSymbols.length > 0 && (
        <CompareLegend
          mainSymbol={chartSymbol}
          comparisonSymbols={comparisonSymbols}
          plotData={chartState.data}
          onRemoveSymbol={(symbol) =>
            removeComparisonSymbol && removeComparisonSymbol(chartId, symbol)
          }
          onClearAll={() => clearComparisons && clearComparisons(chartId)}
          themeColors={themeColors}
        />
      )}
    </div>
  );
});

ChartRenderer.displayName = "ChartRenderer";

ChartRenderer.propTypes = {
  chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  ratio: PropTypes.number,
  type: PropTypes.string,
  chartState: PropTypes.object.isRequired,
  chartConfigs: PropTypes.array.isRequired,
  layoutSnapshot: PropTypes.object,
  orderedCharts: PropTypes.array.isRequired,
  chartSymbol: PropTypes.string,
  periodicity: PropTypes.string,
  showGrid: PropTypes.bool,
  indicatorMenuRef: PropTypes.object,
  chartMenuRef: PropTypes.object,
  handleInteractiveContextMenu: PropTypes.func,
  handleOpenEditModal: PropTypes.func,
  handleMainInteractiveApiReady: PropTypes.func,
  handleInteractiveRemove: PropTypes.func,
  handleRemoveIndicator: PropTypes.func,
  resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleReset: PropTypes.func,
  isPointerInsideChartPanel: PropTypes.bool,
  handleChartCanvasVisibleBarsChange: PropTypes.func,
  handleChartCanvasXExtentsChange: PropTypes.func,
  handleLoadMore: PropTypes.func,
  moveSectionUp: PropTypes.func,
  moveSectionDown: PropTypes.func,
  handleStartResize: PropTypes.func,
  handleResize: PropTypes.func,
  handleEndResize: PropTypes.func,
  perfMonitor: PropTypes.object,
  renkoConfigHash: PropTypes.string,
  pointFigureConfigHash: PropTypes.string,
  // Compare mode
  isCompareMode: PropTypes.bool,
  comparisonSymbols: PropTypes.array,
  removeComparisonSymbol: PropTypes.func,
  clearComparisons: PropTypes.func,
};

export default ChartRenderer;
